import { useEffect, useState } from 'react';
import { useT } from '../i18n/index.js';
import {
  dismissInstallHint,
  installHintDismissed,
  isIos,
  isStandalone,
  promptInstall,
} from '../lib/installPrompt.js';
import { useInstallable } from '../lib/useInstallable.js';

/**
 * The offer to add FilmSloth to the home screen.
 *
 * Shown in exactly two situations, and nowhere near as often as it could be:
 * where the browser has handed us an install event, and on iOS, where it
 * never will and the two taps have to be named. Already installed, already
 * waved away, or a browser that cannot install at all — nothing appears.
 *
 * It sits at the bottom of the home screen rather than over it. An install
 * banner that covers the two buttons somebody came for is the reason people
 * distrust these.
 */
export function InstallCard() {
  const t = useT();
  const installable = useInstallable();
  const [dismissed, setDismissed] = useState(installHintDismissed);
  const [standalone, setStandalone] = useState(isStandalone);
  const [iosHint] = useState(() => isIos() && !isStandalone());

  // Installing does not reload the page, so the card has to notice on its own
  // that it is no longer wanted.
  useEffect(() => {
    const onInstalled = () => setStandalone(true);
    window.addEventListener('appinstalled', onInstalled);
    return () => window.removeEventListener('appinstalled', onInstalled);
  }, []);

  if (standalone || dismissed) return null;
  if (!installable && !iosHint) return null;

  const hide = () => {
    dismissInstallHint();
    setDismissed(true);
  };

  return (
    <div className="panel stack install-card">
      <div className="stack stack--tight">
        <h2>{t('install.title')}</h2>
        <p className="muted small">{installable ? t('install.body') : t('install.iosBody')}</p>
      </div>
      <div className="row">
        {installable ? (
          <button
            type="button"
            className="btn btn--primary btn--block"
            onClick={() => {
              void promptInstall().then((outcome) => {
                // A dismissal is an answer too: asking again on the next
                // visit is how an offer turns into nagging.
                if (outcome === 'dismissed') hide();
              });
            }}
          >
            {t('install.action')}
          </button>
        ) : null}
        <button type="button" className="link small install-card__dismiss" onClick={hide}>
          {t('install.dismiss')}
        </button>
      </div>
    </div>
  );
}
